import {Color} from '@benchling/nucleus-ui';
import {css, StyleSheet} from 'aphrodite';
import React, {useState} from 'react';

import {AnalysisGraphQL} from 'analysis-tool/AnalysisTypes';
import getOutputStepDataForDataFrame from 'analysis-tool/data/getOutputStepDataForDataFrame';
import getStepFromAnalysis from 'analysis-tool/data/getStepFromAnalysis';
import {assert} from 'Asserts';
import {DataFrameColumns} from 'data-frames/DataFrameTypes';
import {
  GetDataFrameColumns,
  GetDataFrameColumnsVariables,
} from 'data-frames/graphql/__generated/GetDataFrameColumns';
import {GET_DATA_FRAME_COLUMNS_QUERY} from 'data-frames/graphql/GetDataFrame';
import getConfig from 'getConfig';
import {AnalyticsVisualizationBarChartType} from 'graphql-client/__generated/graphqlGlobalTypes';
import BenchlingQuery from 'graphql-client/BenchlingQuery';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {AnalyticsVisualization} from 'insights/analytics-visualizations/__generated/AnalyticsVisualization';
import updateVisualization from 'insights/analytics-visualizations/data/updateVisualization';
import {
  VisualizationConfig,
  VisualizationType,
} from 'insights/universal-blocks/SharedUniversalBlockViewTypes';
import staticURL from 'lib/staticURL';
import {FONT_PROXIMA_TITLE} from 'styles/FontConstants';
import {ZINDEX_FLOATING_UI} from 'styles/StyleConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import Tooltip from 'ui-library/bs/Tooltip';
import * as Colors from 'ui-library/Colors';
import Float from 'ui-library/floating-ui/Float';
import {FloatingInteraction, FloatingPlacement} from 'ui-library/floating-ui/FloatingConstants';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import FieldLabel from 'ui-library/inputs/FieldLabel';

import createViewAndOpenRegressionView from '../createViewAndOpenRegressionView';
import createViewAndOpenVisualizationView from '../createViewAndOpenVisualizationView';
import {AnalysisMainViewItem, DataReference} from '../main-view/MainViewTypes';
import {getDefaultVisualizationConfigGQL} from './getDefaultVisualizationConfig';

type AnalyticsVisualizationParameters = AnalyticsVisualization['parameters'];

enum ChartTypeCategory {
  TABLE = 'Table',
  BAR = 'Bar',
  OTHER = 'Other charts',
}

/**
 * ChartOptionType is separate from VisualizationType because it may be more granular than VisualizationType.
 * For example, we allow users to choose between a grouped or stacked bar chart, which both fall under the
 * general VisualizationType.BAR_CHART.
 */
export enum ChartOptionType {
  TABLE = 'Table',
  GROUPED_BAR = 'Vertical grouped bar chart',
  STACKED_BAR = 'Vertical stacked bar chart',
  LINE = 'Line chart',
  PIE = 'Pie chart',
  SCATTER = 'Scatter plot',
  HEATMAP = 'Heatmap',
  REGRESSION = 'Regression',
}

const VISUALIZATION_CHART_TYPES = [
  ChartOptionType.TABLE,
  ChartOptionType.GROUPED_BAR,
  ChartOptionType.STACKED_BAR,
  ChartOptionType.LINE,
  ChartOptionType.PIE,
  ChartOptionType.SCATTER,
  ChartOptionType.HEATMAP,
] as const;

export type VisualizationChartType = (typeof VISUALIZATION_CHART_TYPES)[number];

export const CHART_OPTION_TYPE_TO_VISUALIZATION_TYPE: Record<VisualizationChartType, VisualizationType> = {
  [ChartOptionType.TABLE]: VisualizationType.TABLE,
  [ChartOptionType.GROUPED_BAR]: VisualizationType.BAR_CHART,
  [ChartOptionType.STACKED_BAR]: VisualizationType.BAR_CHART,
  [ChartOptionType.LINE]: VisualizationType.LINE_CHART,
  [ChartOptionType.PIE]: VisualizationType.PIE_CHART,
  [ChartOptionType.SCATTER]: VisualizationType.SCATTER_PLOT,
  [ChartOptionType.HEATMAP]: VisualizationType.HEATMAP,
};

export const CHART_OPTION_TYPE_TO_SVG_PATH: Record<ChartOptionType, string> = {
  [ChartOptionType.TABLE]: '/static/img/insights/table.svg',
  [ChartOptionType.GROUPED_BAR]: '/static/img/insights/grouped_bar_chart_v2.svg',
  [ChartOptionType.STACKED_BAR]: '/static/img/insights/stacked_bar_chart_v2.svg',
  [ChartOptionType.LINE]: '/static/img/insights/line_chart.svg',
  [ChartOptionType.PIE]: '/static/img/insights/pie_chart.svg',
  [ChartOptionType.SCATTER]: '/static/img/insights/scatter_plot.svg',
  [ChartOptionType.HEATMAP]: '/static/img/insights/heatmap.svg',
  [ChartOptionType.REGRESSION]: '/static/img/insights/regression.svg',
};

/** @package */
export const CHART_OPTION_TYPE_TO_CREATE_VIEW_FN: Record<
  ChartOptionType,
  (
    analysis: AnalysisGraphQL,
    inputDataFrameId: string,
    inputDataReference: DataReference,
    onOpenStepInitializationView: (newSelectedItem: AnalysisMainViewItem) => void,
    initialConfig?: VisualizationConfig
  ) => Promise<void>
> = {
  [ChartOptionType.TABLE]: createViewAndOpenVisualizationView,
  [ChartOptionType.GROUPED_BAR]: createViewAndOpenVisualizationView,
  [ChartOptionType.STACKED_BAR]: createViewAndOpenVisualizationView,
  [ChartOptionType.LINE]: createViewAndOpenVisualizationView,
  [ChartOptionType.PIE]: createViewAndOpenVisualizationView,
  [ChartOptionType.SCATTER]: createViewAndOpenVisualizationView,
  [ChartOptionType.HEATMAP]: createViewAndOpenVisualizationView,
  [ChartOptionType.REGRESSION]: createViewAndOpenRegressionView,
};

type ChartOption =
  | {
      optionType: VisualizationChartType;
      imagePath: string;
      createVisualizationConfig: (dataFrameColumns: DataFrameColumns) => VisualizationConfig;
    }
  | {
      optionType: ChartOptionType.REGRESSION;
      imagePath: string;
    };

function makeChartTypeOption(chartOptionType: ChartOptionType): ChartOption {
  const baseChartOption = {
    optionType: chartOptionType,
    imagePath: CHART_OPTION_TYPE_TO_SVG_PATH[chartOptionType],
  };

  switch (chartOptionType) {
    case ChartOptionType.REGRESSION:
      return {optionType: chartOptionType, imagePath: CHART_OPTION_TYPE_TO_SVG_PATH[chartOptionType]};
    case ChartOptionType.TABLE:
    case ChartOptionType.GROUPED_BAR:
    case ChartOptionType.LINE:
    case ChartOptionType.PIE:
    case ChartOptionType.SCATTER:
    case ChartOptionType.HEATMAP:
      return {
        ...baseChartOption,
        createVisualizationConfig: (dataFrameColumns: DataFrameColumns) =>
          getDefaultVisualizationConfigGQL(
            dataFrameColumns,
            CHART_OPTION_TYPE_TO_VISUALIZATION_TYPE[chartOptionType]
          ),
      };
    case ChartOptionType.STACKED_BAR:
      return {
        ...baseChartOption,
        createVisualizationConfig: (dataFrameColumns: DataFrameColumns) => {
          const baseConfig = getDefaultVisualizationConfigGQL(
            dataFrameColumns,
            CHART_OPTION_TYPE_TO_VISUALIZATION_TYPE[chartOptionType]
          );
          return updateVisualization(baseConfig, {
            parameters: {
              ...baseConfig.parameters,
              barChartType: AnalyticsVisualizationBarChartType.STACKED,
            } as AnalyticsVisualizationParameters,
          });
        },
      };
    default:
      assert(false, 'Encountered an unsupported visualization subtype');
  }
}

/**
 * Chart view creation options. The options are grouped under their ChartTypeCategory.
 */
const CHART_TYPE_OPTIONS: Array<{
  chartTypeCategory: ChartTypeCategory;
  chartOptions: Array<ChartOption>;
}> = [
  {
    chartTypeCategory: ChartTypeCategory.TABLE,
    chartOptions: [ChartOptionType.TABLE].map((subtype) => makeChartTypeOption(subtype)),
  },
  {
    chartTypeCategory: ChartTypeCategory.BAR,
    chartOptions: [ChartOptionType.GROUPED_BAR, ChartOptionType.STACKED_BAR].map((subtype) =>
      makeChartTypeOption(subtype)
    ),
  },
  {
    chartTypeCategory: ChartTypeCategory.OTHER,
    chartOptions: [
      ChartOptionType.LINE,
      ChartOptionType.PIE,
      ChartOptionType.SCATTER,
      ChartOptionType.HEATMAP,
      ChartOptionType.REGRESSION,
    ].map((subtype) => makeChartTypeOption(subtype)),
  },
];

/** @package */
export interface ChartButtonProps {
  chartType: ChartOptionType;
  imagePath: string;
  onClick: () => void;
  tooltip: string;
}

export function ChartButton({chartType, imagePath, onClick, tooltip}: ChartButtonProps): JSX.Element {
  return (
    <Tooltip title={tooltip}>
      <div
        className={css(styles.chartOption)}
        onClick={onClick}
        {...getTestProps({component: 'ChartButton', element: 'root', key: chartType})}
      >
        <img className={css(styles.chartImg)} src={staticURL(imagePath)} />
      </div>
    </Tooltip>
  );
}

export interface AddChartViewPopoverProps {
  analysis: AnalysisGraphQL;
  stepId: string;
  onOpenStepInitializationView: (newSelectedItem: AnalysisMainViewItem) => void;
  dataFrameId: string;
  children: (openMenu: () => void) => JSX.Element;
}

export function AddChartViewMenu({
  analysis,
  stepId,
  onOpenStepInitializationView,
  dataFrameId,
  onRequestClose,
}: Omit<AddChartViewPopoverProps, 'children'> & {onRequestClose: () => void}): JSX.Element {
  const step = getStepFromAnalysis(analysis, stepId);

  const datasetOutputStepData = getOutputStepDataForDataFrame(step, dataFrameId);
  assert(
    datasetOutputStepData != null,
    "Data frame's step has no output step data that contains the data frame"
  );
  const inputDataReference: DataReference = {stepId: step.id, stepDataName: datasetOutputStepData.name};

  return (
    <div
      className={css(styles.selectViewPopover)}
      {...getTestProps({component: 'AddChartViewMenu', element: 'root'})}
    >
      <BenchlingQuery<GetDataFrameColumns, GetDataFrameColumnsVariables>
        query={GET_DATA_FRAME_COLUMNS_QUERY}
        variables={{dataFrameId}}
        renderLoading={() => <CenteredHorizontalThrobber />}
      >
        {({dataFrame}) => {
          assert(!isInaccessible(dataFrame), 'User does not have access to input data frame');
          return CHART_TYPE_OPTIONS.map((chartTypeOption) => (
            <div className={css(styles.chartTypeSection)} key={chartTypeOption.chartTypeCategory}>
              <FieldLabel label={chartTypeOption.chartTypeCategory.toUpperCase()} />
              <div className={css(styles.chartOptionsSection)}>
                {chartTypeOption.chartOptions
                  .filter(
                    (chartOption) =>
                      getConfig('ENABLE_ANALYSIS_TOOL_REGRESSIONS') ||
                      chartOption.optionType !== ChartOptionType.REGRESSION
                  )
                  .map((chartOption) => (
                    <ChartButton
                      chartType={chartOption.optionType}
                      key={chartOption.optionType}
                      tooltip={String(chartOption.optionType)}
                      imagePath={chartOption.imagePath}
                      onClick={() => {
                        const initialConfig =
                          chartOption.optionType === ChartOptionType.REGRESSION
                            ? undefined
                            : chartOption.createVisualizationConfig(dataFrame.allColumnsJson);

                        CHART_OPTION_TYPE_TO_CREATE_VIEW_FN[chartOption.optionType](
                          analysis,
                          dataFrame.id,
                          inputDataReference,
                          onOpenStepInitializationView,
                          initialConfig
                        );

                        onRequestClose();
                      }}
                    />
                  ))}
              </div>
            </div>
          ));
        }}
      </BenchlingQuery>
    </div>
  );
}

/**
 * Popover to select and add a chart view of a dataset in an analysis.
 */
export default function AddChartViewPopover({
  analysis,
  stepId,
  onOpenStepInitializationView,
  dataFrameId,
  children,
}: AddChartViewPopoverProps): JSX.Element {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <Float
      isOpen={isOpen}
      onRequestClose={() => {
        setIsOpen(false);
      }}
      interaction={FloatingInteraction.CONTROLLED}
      placement={FloatingPlacement.BOTTOM_END}
      floatingElementOptions={{
        floatingBodyZIndex: ZINDEX_FLOATING_UI,
        floatingBodyClassName: css(styles.popdownBody),
      }}
      renderContent={() => (
        <AddChartViewMenu
          analysis={analysis}
          stepId={stepId}
          onOpenStepInitializationView={onOpenStepInitializationView}
          dataFrameId={dataFrameId}
          onRequestClose={() => {
            setIsOpen(false);
          }}
        />
      )}
    >
      {children(() => {
        setIsOpen(true);
      })}
    </Float>
  );
}

const styles = StyleSheet.create({
  chartImg: {
    width: 46,
    height: 25,
    margin: 'auto',
  },
  chartOptionsSection: {
    display: 'flex',
    flexWrap: 'wrap',
  },
  chartOption: {
    width: 72,
    height: 48,
    display: 'inline-flex',
    borderRadius: 4,
    marginRight: 4,
    marginBottom: 8,
    backgroundColor: Color.GRAY_50,
    border: '1px solid',
    borderColor: Color.GRAY_200,
    cursor: 'pointer',
    ':hover': {
      backgroundColor: Color.GRAY_200,
      borderColor: Color.GRAY_200,
    },
  },
  chartTypeSection: {
    marginBottom: 8,
  },
  popdownBody: {
    background: Colors.WHITE,
    borderRadius: 4,
    boxShadow: `0px 2px 6px ${Colors.GRAY_DARKER}`,
  },
  sectionTitle: {
    ...FONT_PROXIMA_TITLE,
    marginBotton: 8,
  },
  selectViewPopover: {
    paddingTop: 16,
    paddingLeft: 16,
    // Actual paddingRight ends up being 16 because of the clickableChartOption marginRight (4)
    paddingRight: 12,
    // paddingBottom is not defined, but ends up being 16 because of clickableChartOption marginBottom (8) and
    // chartTypeSection marginBottom (8)
    width: 256,
  },
});
